import { useForm } from "react-hook-form";
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resetPasswordAPI } from "../services/authService";

import logo from "../assets/images/logo.png";
import logo_2 from "../assets/images/logo-3.png";
import Background1 from "../assets/images/Slider 1.jpg";
import Background2 from "../assets/images/Slider 2.jpg";
import Background3 from "../assets/images/Slider 3.jpg";
import Background4 from "../assets/images/Slider 4.jpg";
import { toast } from "react-hot-toast";
import { Eye, EyeOff } from "lucide-react";

function ResetPassword() {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const backgrounds = [Background1, Background2, Background3, Background4];

  const token = new URLSearchParams(location.search).get("token");
  const newPassword = watch("newPassword");

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % backgrounds.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

const onSubmit = async (data) => {
  setIsLoading(true);

  try {
    const res = await resetPasswordAPI(data.newPassword, token);
    if (res.data.status === "success") {
      setStatus("success");
      toast.success("Password reset successful!");
      setTimeout(() => navigate("/login"), 2000);
    } else {
      setStatus("failed");
      // Display the specific error message from backend or fallback to default
      toast.error(res.data.message || "Invalid or expired token.");
    }
  } catch (err) {
    console.error(err);
    setStatus("error");

    // Check if the error has a response with data from the backend
    if (err.response && err.response.data) {
      // Handle backend errors with specific messages (like 400, 404 statuses)
      if (err.response.data.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("Something went wrong. Try again later.");
      }
    }
    // Handle network errors
    else if (err.message === "Network Error") {
      toast.error("Network error. Please check your connection.");
    }
    // Fallback to generic error message
    else {
      toast.error("Something went wrong. Try again later.");
    }
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 w-full h-full z-0">
        {backgrounds.map((bg, index) => (
          <img
            key={index}
            src={bg}
            alt={`Background ${index + 1}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentBgIndex ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>

      <div className="relative flex flex-col gap-8 z-10 flex items-center justify-center w-full h-full px-4">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="relative w-full max-w-md rounded-2xl bg-white/70 shadow-xl p-10 space-y-6 text-center z-10"
          noValidate
        >
          <img src={logo} alt="Logo" className="mx-auto w-50 pb-2" />
          <h2 className="text-gray-800 text-xl font-semibold">
            Reset Your Password
          </h2>
          <p className="text-gray-800 text-sm">
            Enter your new password to complete the reset.
          </p>

          <div className="space-y-4">
            <div className="relative">
              <input
                {...register("newPassword", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                  pattern: {
                    value: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
                    message:
                      "Password must include at least one special character",
                  },
                })}
                type={showNewPassword ? "text" : "password"}
                placeholder="New Password"
                className={`w-full px-4 py-3 pr-10 rounded-full text-sm text-black placeholder:text-black/60 bg-white/90 border border-white/30 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#27ade3] transition-all ${
                  errors.newPassword ? "border-red-500 focus:ring-red-500" : ""
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-6 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {errors.newPassword && (
                <p className="text-red-500 text-xs mt-1 text-left ml-4">
                  {errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="relative">
              <input
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === newPassword || "Passwords do not match",
                })}
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                className={`w-full px-4 py-3 pr-10 rounded-full text-sm text-black placeholder:text-black/60 bg-white/90 border border-white/30 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-[#27ade3] transition-all ${
                  errors.confirmPassword
                    ? "border-red-500 focus:ring-red-500"
                    : ""
                }`}
              />
              <button
                type="button"
                className="absolute right-3 top-6 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1 text-left ml-4">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 rounded-full bg-gradient-to-r from-[#1a325d] to-[#27ade3] 
              text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300 
              border border-[#27ade3] hover:border-white/50
              hover:scale-[1.02] active:scale-95
              relative overflow-hidden group ${
                isLoading ? "opacity-80 cursor-not-allowed" : ""
              }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Resetting...
              </div>
            ) : (
              <>
                <span className="relative z-10">Reset Password</span>
                <span className="absolute inset-0 bg-[#27ade3]/30 opacity-0 group-hover:opacity-100 transition duration-300 blur-sm"></span>
              </>
            )}
          </button>
        </form>

        <img src={logo_2} alt="Logo" className="mx-auto w-50 pb-2" />
      </div>
    </div>
  );
}

export default ResetPassword;
